/**
 * TanStack Query hooks for the Phase 4 Perspectives API.
 *
 * Three hooks:
 * - `useCatalog()` — the static `/api/perspectives/catalog` list.
 *   Cached indefinitely via `staleTime: Infinity` because the catalog
 *   only changes on Alembic migrations (new perspectives), which
 *   requires an app reload anyway.
 * - `useChildPerspectives(childId)` — computed per-child merged view.
 *   Reads `/api/children/{id}/perspectives`. Stale after 30s to
 *   match the dashboard "fresh every open" intuition.
 * - `useUpdateChildPreferences(childId)` — bulk pin/hide/reorder with
 *   an optimistic cache mutation: we update the in-memory list the
 *   moment the user clicks pin, before the server has confirmed.
 *   Rolls back on error by invalidating the query.
 *
 * Cache keys:
 *   ["perspectives", "catalog"]
 *   ["perspectives", "child", childId]
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PerspectiveCatalogEntry {
  slug: string;
  name: string;
  tier: 1 | 2 | 3 | 4 | 5;
  unit: string;
  default_visible: boolean;
  description: string;
  research_anchor: string | null;
  display_order: number;
}

export type PerspectiveConfidence = "exact" | "estimated";
export type PerspectiveVisualHint =
  | "dot_grid"
  | "bar"
  | "ring"
  | "number"
  | "meter";

export interface ComputedPerspective {
  slug: string;
  name: string;
  tier: 1 | 2 | 3 | 4 | 5;
  value: number;
  unit: string;
  visual_hint: PerspectiveVisualHint;
  caption: string;
  confidence: PerspectiveConfidence;
  expired: boolean;
  pinned: boolean;
  hidden: boolean;
  display_order: number;
  metadata: Record<string, unknown>;
}

export interface PreferencePatch {
  slug: string;
  pinned?: boolean;
  hidden?: boolean;
  display_order?: number;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const CATALOG_QUERY_KEY = ["perspectives", "catalog"] as const;
export const childPerspectivesKey = (childId: string) =>
  ["perspectives", "child", childId] as const;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useCatalog() {
  return useQuery<PerspectiveCatalogEntry[], Error>({
    queryKey: CATALOG_QUERY_KEY,
    queryFn: () => apiFetch<PerspectiveCatalogEntry[]>("/api/perspectives/catalog"),
    staleTime: Infinity,
  });
}

export function useChildPerspectives(childId: string | null | undefined) {
  return useQuery<ComputedPerspective[], Error>({
    queryKey: childId ? childPerspectivesKey(childId) : ["perspectives", "child", "none"],
    queryFn: () => {
      if (typeof childId !== "string" || childId.length === 0) {
        return Promise.resolve([]);
      }
      return apiFetch<ComputedPerspective[]>(
        `/api/children/${childId}/perspectives`,
      );
    },
    enabled: typeof childId === "string" && childId.length > 0,
    staleTime: 30_000,
  });
}

interface UpdateVariables {
  entries: PreferencePatch[];
}

interface UpdateContext {
  previous: ComputedPerspective[] | undefined;
}

export function useUpdateChildPreferences(childId: string) {
  const queryClient = useQueryClient();
  const queryKey = childPerspectivesKey(childId);

  return useMutation<
    ComputedPerspective[],
    Error,
    UpdateVariables,
    UpdateContext
  >({
    mutationFn: ({ entries }) =>
      apiFetch<ComputedPerspective[]>(
        `/api/children/${childId}/perspectives`,
        {
          method: "PATCH",
          body: { entries },
        },
      ),
    onMutate: async ({ entries }) => {
      // Cancel any in-flight refetch so we don't race our optimistic update.
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ComputedPerspective[]>(queryKey);

      if (previous !== undefined) {
        // Apply each patch to the in-memory copy. We do NOT recompute
        // the sort order — the optimistic view might be slightly out
        // of order until the server response lands, but pin/unpin
        // feedback is instant.
        const patched = previous.map((p) => {
          const entry = entries.find((e) => e.slug === p.slug);
          if (entry === undefined) return p;
          return {
            ...p,
            pinned: entry.pinned ?? p.pinned,
            hidden: entry.hidden ?? p.hidden,
            display_order: entry.display_order ?? p.display_order,
          };
        });

        // Filter hidden so the optimistic UI matches the server's filter.
        queryClient.setQueryData<ComputedPerspective[]>(
          queryKey,
          patched.filter((p) => !p.hidden),
        );
      }

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSuccess: (data) => {
      // Server response is authoritative — overwrite the cache.
      queryClient.setQueryData(queryKey, data);
    },
    onSettled: () => {
      // Belt-and-suspenders: refetch in the background in case
      // another device updated the prefs in the meantime.
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}
